

































































































import {
  SfLoader, SfImage, SfRadio, SfButton,
} from '~/components/StorefrontUi';
import PickupPoints from '~/modules/checkout/components/ShippingAndPayment/PickupPoints.vue';

import {
  computed,
  defineComponent,
  ref,
  onMounted,
  onUnmounted,
  useContext,
} from '@nuxtjs/composition-api';
import type { PropType } from '@nuxtjs/composition-api';
import cartGetters from '~/modules/checkout/getters/cartGetters';
import useCart from '~/modules/checkout/composables/useCart';
import useShippingProvider from '~/modules/checkout/composables/useShippingProvider';
import getShippingMethodPrice from '~/helpers/checkout/getShippingMethodPrice';

import type {
  ShippingCartAddress,
  SelectedShippingMethod,
  AvailableShippingMethod,
} from '~/modules/GraphQL/types';
import { getShippingIconByTitle } from '~/helpers/getShippingIconByTitle';
import useShipping from '~/modules/checkout/composables/useShipping';
import { useModals } from '~/modules/catalog/product/composables/useModals';

interface PickupAddress {
  city: string;
  street: string;
  postcode: string;
  parcel_locker_no?: string;
  is_cash_on_delivery?: boolean;
}

export default defineComponent({
  name: 'VsfShippingProvider',
  components: {
    SectionHeading: () => import(
      /* webpackPrefetch: true */ '~/modules/checkout/components/SectionHeading.vue'
    ),
    SfRadio,
    SfButton,
    PickupPoints,
    SfLoader,
    SfImage,
  },
  props: {
    shippingMethods: {
      type: Array as PropType<AvailableShippingMethod[]>,
      default: () => [],
    },
  },
  setup(props, { emit }) {
    const { env } = useContext();
    const { cart } = useCart();
    const { closeModals, isPickupPointPopup, openModal } = useModals();
    const shippingDetails = ref<ShippingCartAddress | null>(null);
    const state = ref<SelectedShippingMethod | null>(null);
    const { load: loadShipping } = useShipping();
    const {
      save: saveShippingProvider,
      error: errorShippingProvider,
      loading: isLoading,
    } = useShippingProvider();

    const selectedShippingMethod = computed(() => state.value);
    const isPickupPointRequired = computed(
      () => props.shippingMethods.find(
        (m) => m.carrier_code === selectedShippingMethod.value?.carrier_code,
      )?.pickup_point_required,
    );
    const totals = computed(() => cartGetters.getTotals(cart.value));
    const isShippingMethodStepCompleted = computed(
      () => state.value?.method_code && !isLoading.value,
    );
    const pickupAddress = ref<PickupAddress>({
      city: '',
      street: '',
      postcode: '',
      parcel_locker_no: '',
      is_cash_on_delivery: false,
    });
    const pickupProvider = ref('');
    const lastSelectedShippingMethodCodeWithPickup = ref('');
    const isShowPickupInfo = computed(() => pickupAddress.value?.city);

    const resetPickupPoint = () => {
      pickupAddress.value = {
        city: '',
        street: '',
        postcode: '',
        parcel_locker_no: '',
        is_cash_on_delivery: false,
      };
    };
    const handlePickupPointsPopupOpen = () => {
      // TODO: refactor. Don't use hardcoded carrier codes
      if (state.value.carrier_code === env.VSF_MAGENTO_SHIPPING_CODE_INPOST) {
        pickupProvider.value = 'In Post';
      } else if (state.value.carrier_code === env.VSF_MAGENTO_SHIPPING_CODE_PP) {
        pickupProvider.value = 'Poczta Polska';
      }
      openModal('pickuppoints');
    };

    const selectShippingMethod = async (
      method: AvailableShippingMethod | SelectedShippingMethod,
      shouldResetPickup: boolean = true,
    ) => {
      // @ts-ignore
      const isCurrentMethodPickupRequired = method?.pickup_point_required;

      const shippingData = {
        carrier_code: method.carrier_code,
        method_code: method.method_code,
        pickup_address: {
          firstname: shouldResetPickup ? '' : 'Paczkomat',
          lastname: shouldResetPickup ? '' : pickupAddress.value.parcel_locker_no || '',
          street: shouldResetPickup ? '' : pickupAddress.value.street || '',
          city: shouldResetPickup ? '' : pickupAddress.value.city || '',
          postcode: shouldResetPickup ? '' : pickupAddress.value.postcode || '',
          country_code: shouldResetPickup ? '' : 'PL',
          is_cash_on_delivery: shouldResetPickup ? false : pickupAddress.value.is_cash_on_delivery || false,
          parcel_locker_no: shouldResetPickup ? '' : pickupAddress.value.parcel_locker_no || '',
        },
      };

      state.value = await saveShippingProvider({
        shippingMethod: shippingData,
      });

      // we reset pickupAddress, pickupId after saveShippingProvider,
      // so this info doesn't dissapear immidiately
      if (shouldResetPickup) resetPickupPoint();
      lastSelectedShippingMethodCodeWithPickup.value = isCurrentMethodPickupRequired ? method.carrier_code : '';
      // if pickup point is required and user didn't select it
      if (isCurrentMethodPickupRequired && !pickupAddress.value.postcode) {
        emit('status');
      } else {
        emit('status', method);
      }
    };

    const handlePickupMessageEvent = (e: any) => {
      if (!env.PICKUP_POINTS_MAP_URL.includes(e.origin as string)) return;
      if (!e?.data) {
        closeModals();
        return;
      }
      const doesDataExist = Object.keys(e.data as object).length > 0;
      if (doesDataExist) {
        const result = e.data;
        pickupProvider.value = result.pickup_type;
        pickupAddress.value = {
          city: result.city,
          street: result.street,
          postcode: result.zip,
          parcel_locker_no: result.points.id,
          is_cash_on_delivery: result.points.features.cash_on_delivery,
        };

        const carrierCode = pickupProvider.value === 'In Post'
          ? env.VSF_MAGENTO_SHIPPING_CODE_INPOST
          : env.VSF_MAGENTO_SHIPPING_CODE_PP;
        const method = props.shippingMethods.find(
          (m) => m.carrier_code === carrierCode,
        );
        lastSelectedShippingMethodCodeWithPickup.value = carrierCode;
        selectShippingMethod(method, false);
      }
      closeModals();
    };

    const pickupAddressToShowAsDefaultInWidget = ref({
      city: '',
      street: '',
      postcode: '',
    });

    onMounted(async () => {
      shippingDetails.value = await loadShipping();
      if (
        shippingDetails.value?.selected_shipping_method?.carrier_code.trim()
      ) {
        state.value = shippingDetails.value.selected_shipping_method;

        const address = shippingDetails.value.selected_shipping_method.pickup_address;
        if (address.city) {
          lastSelectedShippingMethodCodeWithPickup.value = shippingDetails.value.selected_shipping_method.carrier_code;
          pickupAddress.value = {
            city: address.city,
            street: address.street,
            postcode: address.postcode,
            parcel_locker_no: shippingDetails.value.selected_shipping_method.pickup_address?.parcel_locker_no,
            is_cash_on_delivery: shippingDetails.value.selected_shipping_method.pickup_address?.is_cash_on_delivery,
          };
        }

        pickupAddressToShowAsDefaultInWidget.value = {
          city: shippingDetails.value.city || '',
          street: shippingDetails.value.street[0] || '',
          postcode: shippingDetails.value.postcode || '',
        };
        emit('status', state.value);
      }

      window.addEventListener('message', handlePickupMessageEvent);
    });
    onUnmounted(() => {
      window.removeEventListener('message', handlePickupMessageEvent);
    });
    return {
      errorShippingProvider,
      getShippingMethodPrice,
      isLoading,
      isShippingMethodStepCompleted,
      selectedShippingMethod,
      selectShippingMethod,
      // state,
      totals,
      getShippingIconByTitle,
      closeModals,
      isPickupPointPopup,
      handlePickupPointsPopupOpen,
      pickupAddress,
      isShowPickupInfo,
      pickupProvider,
      isPickupPointRequired,
      pickupAddressToShowAsDefaultInWidget,
    };
  },
});
